import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resetIdentityReviewed } from '../../../utils/identityNudge';

const TERMS_ACCEPTANCE_STORAGE_KEY = 'tenderaTermsAcceptance';
// Exported so useOrganizationIdentityStatus can check/clear the same key.
export const IDENTITY_BUILD_PENDING_KEY = 'tenderaIdentityBuildPending';

const getApiErrorMessage = (error) => {
    const data = error?.data;

    if (!data) return error?.message || null;
    if (typeof data === 'string') return data;

    if (typeof data.userFriendlyMessage === 'string' && data.userFriendlyMessage.trim()) {
        return data.userFriendlyMessage;
    }
    if (typeof data.message === 'string' && data.message.trim()) {
        return data.message;
    }
    if (Array.isArray(data.errors) && data.errors.length > 0) {
        return data.errors
            .map((item) => {
                if (typeof item === 'string') return item;
                if (typeof item?.message === 'string') return item.message;
                return null;
            })
            .filter(Boolean)
            .join(', ');
    }

    return error?.message || null;
};

/**
 * Custom hook to handle final onboarding submission
 * Processes form data, suggestions feedback, and completes onboarding
 */
const useOnboardingSubmit = (callApi, updateOnboardingStatus, refreshOnboardingStatus, language) => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    /**
     * Process suggestions feedback and extract accepted items
     */
    const processAcceptedSuggestions = (suggestionsResult) => {
        if (!suggestionsResult?.suggestions || !suggestionsResult?.feedback) {
            return null;
        }

        const { suggestions, feedback } = suggestionsResult;

        // Extract accepted keywords
        const acceptedKeywords = feedback.keywordFeedback
            ?.filter(k => k.userAction === 'ACCEPTED' || k.userAction === 'CUSTOM_ADDED')
            .map(k => k.keyword) || [];

        // Extract accepted CPV codes with searchNested flag
        const acceptedCpvCodes = feedback.cpvCodeFeedback
            ?.filter(c => c.userAction === 'ACCEPTED' || c.userAction === 'CUSTOM_ADDED')
            .map(c => ({
                cpvCode: c.cpvCode,
                searchNested: c.searchNested !== undefined ? c.searchNested : true
            })) || [];

        // Extract accepted NUTS codes with searchNested flag
        const acceptedNutsCodes = feedback.nutsCodeFeedback
            ?.filter(n => n.userAction === 'ACCEPTED' || n.userAction === 'CUSTOM_ADDED')
            .map(n => ({
                nutsCode: n.nutsCode,
                searchNested: n.searchNested !== undefined ? n.searchNested : true
            })) || [];

        // Build the search profile if there are any accepted suggestions
        if (acceptedKeywords.length > 0 || acceptedCpvCodes.length > 0 || acceptedNutsCodes.length > 0) {
            return {
                keywords: acceptedKeywords,
                cpvCodes: acceptedCpvCodes,
                nutsCodes: acceptedNutsCodes,
                sessionId: suggestionsResult.sessionId
            };
        }

        return null;
    };

    /**
     * Clear search-related localStorage
     */
    const clearSearchCache = () => {
        const keysToRemove = [
            'noticeSearchForm',
            'noticeSearchResults',
            'noticeSearchPage',
            'noticeSearchExpandedRows',
            'noticeSearchTotalCount',
            'noticeSearchSortBy',
            'noticeSearchSortOrder',
            'selectedSearchProfileId',
            'userClearedSearchProfile',
            'hasSearchBeenRun'
        ];

        keysToRemove.forEach(key => localStorage.removeItem(key));
    };

    const getTermsAcceptance = () => {
        const raw = localStorage.getItem(TERMS_ACCEPTANCE_STORAGE_KEY);
        if (!raw) return null;
        try {
            return JSON.parse(raw);
        } catch (error) {
            return null;
        }
    };

    /**
     * Submit onboarding data to backend
     */
    const submitOnboarding = async (userId, formData, suggestionsResult = null, termsAcceptanceOverride = null) => {
        setIsSubmitting(true);
        setSubmitError(null);

        try {
            const termsAcceptance = termsAcceptanceOverride || getTermsAcceptance();
            if (!termsAcceptance?.termsVersion) {
                throw new Error('Terms and Conditions must be accepted before completing onboarding.');
            }

            await callApi('POST', '/api/legal/terms/accept', {
                termsVersion: termsAcceptance.termsVersion,
                language: termsAcceptance.language || language
            });

            // Build accepted suggestions data
            const acceptedSuggestions = processAcceptedSuggestions(suggestionsResult);
            const organizationInfo =
                suggestionsResult?.organizationInfo ||
                suggestionsResult?.suggestions?.organizationInfo ||
                null;
            const organizationName = formData.organizationName?.trim() || organizationInfo?.officialName || '';
            const organizationBusinessCode =
                formData.organizationBusinessCode?.trim() ||
                organizationInfo?.businessCode ||
                formData.vat?.trim() ||
                '';

            if (!organizationName) {
                throw new Error('Organization details could not be resolved. Please go back and retry the organization lookup.');
            }

            const payload = {
                hankoUserId: userId,
                firstName: formData.firstName?.trim(),
                lastName: formData.lastName?.trim(),
                organizationName,
                organizationBusinessCode,
                companyDescription: formData.companyDescription?.trim() || undefined,
                language: language
            };

            if (acceptedSuggestions) {
                payload.acceptedSuggestions = acceptedSuggestions;
            }

            // Submit to backend
            await callApi('POST', '/api/onboarding', payload);

            // Clear search cache to avoid confusion
            clearSearchCache();
            localStorage.removeItem(TERMS_ACCEPTANCE_STORAGE_KEY);

            // Marks that the background OrganizationIdentity enrichment job (java-microservice-bff
            // OrganizationIdentityEnrichmentService) may still be in flight, so
            // useOrganizationIdentityStatus knows to poll and show a "building" indicator. Without
            // this, every already-onboarded account (which will never have an identity) would poll
            // for up to 10 minutes on every page load with nothing to show for it.
            localStorage.setItem(IDENTITY_BUILD_PENDING_KEY, 'true');
            resetIdentityReviewed();

            // Update auth state
            updateOnboardingStatus(true);

            // First landing after onboarding goes to the search view with the
            // freshly-created profile applied as a filter. Subsequent logins
            // hit the home view via the normal post-login flow.
            navigate('/search?fromOnboarding=true');

            return { success: true };
        } catch (error) {
            console.error('Onboarding submission failed:', error, {
                status: error?.status,
                data: error?.data,
                response: error?.response
            });

            if (typeof refreshOnboardingStatus === 'function') {
                try {
                    const onboardingComplete = await refreshOnboardingStatus();
                    if (onboardingComplete) {
                        clearSearchCache();
                        localStorage.removeItem(TERMS_ACCEPTANCE_STORAGE_KEY);
                        updateOnboardingStatus(true);
                        navigate('/search?fromOnboarding=true');
                        return {
                            success: true,
                            recovered: true
                        };
                    }
                } catch (refreshError) {
                    console.error('Failed to verify onboarding status after submission error:', refreshError);
                }
            }

            const errorMessage = getApiErrorMessage(error) || 'onboarding.errors.failed';
            setSubmitError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        isSubmitting,
        submitError,
        submitOnboarding,
        setSubmitError
    };
};

export default useOnboardingSubmit;
